/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts JS
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import MoserJS from 'bi/moser/moser.min'
import StringResources from '../nls/StringResources'

class MetadataService {

	/**
	 * The constructor that gets initialized automatically by the glass service.
	 */
	constructor() {
		this.moduleId = null
		this.request = null
		this.columnList = null
	}

	/**
	 * Initialize gets called by the glass component once the service has been loaded.
	 * @param {object} glassContext - The glassContext provided by the glass service.
	 * @returns {promise} Returns a promise
	 */
	initialize(glassContext) {
		this.glassContext = glassContext
		this.logger = this.glassContext.getCoreSvc('.Logger')
		return Promise.resolve()
	}

	/**
	 * Fetches and saves the metadata for a specified module.
	 * @param {string} moduleId - The module ID for a specified module.
	 * @param {string} [sourceType] - The sourceType for a specified module (optional).
	 * @returns {promise} Returns a promise that finishes once the metadata is loaded.
	 */
	loadMetadata(moduleId, sourceType) {
		if (!moduleId) {
			const message = StringResources.get('errorMissingModuleId')
			return Promise.reject(new Error(message))
		}

		if (this.moduleId === moduleId) {
			return this.request || Promise.resolve()
		}

		// Clear the cache to avoid loading the data from the previous source.
		this.clearCache()

		// If we know the source type, send it to Moser for a minor performance improvement.
		let url = `v1/metadata/modules/${moduleId}/metadata?shortIdForExpression=true&item=on`
		if (sourceType) {
			url += `&type=${sourceType}`
		}

		this.request = this._request(url)
			.then(response => this._cacheMetadata(moduleId, response.data))
			.catch(error => {
				const message = StringResources.get('errorUnableToLoadMetadata')
				this.logger.error(message, error)
				throw new Error(message)
			})

		return this.request
	}

	/**
	 * Saves the metadata for a specified module. This is useful if you have already fetched the metadata module.
	 * @param {string} moduleId - The module ID for a specified module
	 * @param {object} metadata - The metadata source file
	 */
	loadMetadataFromSource(moduleId, metadata) {
		if (moduleId && metadata) {
			this._cacheMetadata(moduleId, metadata)
		} else {
			const message = StringResources.get('errorMissingModuleIdMetadataParameters')
			this.logger.error(message)
			throw new Error(message)
		}
	}

	/**
	 * Gets the column info (label and ancestors).
	 * @param {array} [columnIds] - The column IDs for a specified module (optional).
	 * @returns {array | null} Returns an array containing metadata info for each column.
	 */
	getColumnInfo(columnIds = []) {
		if (this.moduleId && this.columnList) {
			const columnInfo = this._getColumnInfos(columnIds)
			return Promise.resolve(columnInfo)
		}

		if (this.request) {
			return this.request.then(() => this._getColumnInfos(columnIds))
		}

		// Module not loaded - throw error
		const message = StringResources.get('errorModuleNotYetLoaded')
		this.logger.error(message)
		return Promise.reject(new Error(message))
	}

	_getColumnInfos(columnIds) {
		const matches = {}

		this.columnList.forEach(column => {
			if (columnIds.indexOf(column.idForExpression) !== -1 || columnIds.length === 0) {
				const ancestors = this._getColumnAncestors(column)
				const columnProperties = this._getColumnProperties(column)

				if (matches[ancestors]) {
					matches[ancestors].push(columnProperties)
				} else {
					matches[ancestors] = [columnProperties]
				}
			}
		})

		const columnInfo = Object.keys(matches).map(ancestors => {
			return {
				ancestors: ancestors.split('|'),
				items: matches[ancestors]
			}
		})

		return columnInfo
	}

	/**
	 * Clears the moduleId and metadata from the cache.
	 */
	clearCache() {
		this.moduleId = null
		this.request = null
		this.columnList = null
	}

	/**
	 * Caches all the MoserJS properties locally for future use.
	 * @param {string} moduleId - The module ID
	 * @param {object} metadata - The metadata object from Moser
	 */
	_cacheMetadata(moduleId, metadata) {
		const factory = MoserJS.createObjectFactory()
		const jsonFactory = MoserJS.createJSONObjectFactory(factory)
		const module = jsonFactory.createModule(metadata)
		this.moduleId = moduleId
		this.columnList = MoserJS.ModuleUtils.getTreeChildren(module, 0 /* unlimited */, [MoserJS.MoserObjectTypes.QUERY_ITEM])
	}

	/**
	 * Gets the column ancestor structure as a string.
	 * @param {object} column - The column that we will get the ancestor structure for.
	 * @returns {string} Returns an string containing the ancestor structure for the specified column.
	 */
	_getColumnAncestors(column) {
		let ancestors = ''
		let tree = MoserJS.ModuleUtils.getTreeAncestor(column)

		while (tree && tree.getObjectType() !== 'Module') {
			ancestors = ancestors ? `${tree.label}|${ancestors}` : tree.label
			tree = MoserJS.ModuleUtils.getTreeAncestor(tree)
		}

		return ancestors
	}

	/**
	 * Formats the metadata column info into a new object.
	 * @param {object} column - The Moser column object.
	 * @returns {object} Returns an object containing the desired metadata info.
	 */
	_getColumnProperties({ label, idForExpression }) {
		return { idForExpression, label }
	}

	/**
	 * Creates a GET request with the proper parameters.
	 * @param {string} url - The URL that we will be sending the GET request.
	 * @returns {promise} Returns a promise that finishes once the server has responded.
	 */
	_request(url) {
		return this.glassContext.getCoreSvc('.Ajax').ajax({
			type: 'GET',
			url: url,
			contentType: 'application/json',
			xhrFields: {
				withCredentials: true
			}
		})
	}

}

module.exports = MetadataService
